/**
 * Sudoku: a solution counter, a human-technique rater and a generator.
 *
 * Every puzzle the generator returns has exactly one solution — a backtracking solver
 * counts solutions and stops at two, so "unique" is proved, not hoped for. Difficulty is
 * rated by the hardest technique a human solver is forced to use, not by the number of
 * givens: the rater solves the grid the way a person would, always taking the easiest
 * available step first, and records the hardest technique it needed.
 *
 * Tiers (by the hardest technique needed):
 *   1 Easy    — naked and hidden singles only
 *   2 Medium  — adds locked candidates (pointing and claiming)
 *   3 Hard    — adds naked and hidden pairs and triples
 *   4 Expert  — needs an X-Wing, or something beyond every technique the rater knows
 *
 * All randomness comes from the seeded `Rng` the caller passes, so a puzzle is a pure
 * function of (seed, difficulty, symmetry) and can be regenerated from the number printed
 * on the page.
 */

import {
  createRng,
  type Rng,
  shuffled,
} from "@/lib/tool-kit/worksheets/random";

// biome-ignore-start lint/suspicious/noBitwiseOperators: candidate sets are bit masks, one bit per digit
export type Difficulty = "easy" | "medium" | "hard" | "expert";
export type Technique =
  | "naked-single"
  | "hidden-single"
  | "locked-candidates"
  | "naked-subset"
  | "hidden-subset"
  | "x-wing";

export const DIFFICULTIES: readonly Difficulty[] = [
  "easy",
  "medium",
  "hard",
  "expert",
];
export const TECHNIQUES: readonly Technique[] = [
  "naked-single",
  "hidden-single",
  "locked-candidates",
  "naked-subset",
  "hidden-subset",
  "x-wing",
];

export const CELLS = 81;
const ALL_DIGITS = 0x1_ff;
/** Tier a technique belongs to: 1 singles, 2 locked candidates, 3 subsets, 4 X-Wing. */
const TECHNIQUE_TIER: Record<Technique, number> = {
  "naked-single": 1,
  "hidden-single": 1,
  "locked-candidates": 2,
  "naked-subset": 3,
  "hidden-subset": 3,
  "x-wing": 4,
};
export const TIER_OF: Record<Difficulty, number> = {
  easy: 1,
  medium: 2,
  hard: 3,
  expert: 4,
};
/** Tier given to a puzzle the rater cannot finish: beyond expert. */
export const BEYOND_TIER = 5;
const MAX_ATTEMPTS = 300;
/** Expert looks for an X-Wing puzzle this many times before settling for harder. */
const XWING_ATTEMPTS = 500;
/** Easy puzzles stop losing givens here so they stay friendly. */
const EASY_STOP_AT = 38;

/* ------------------------------------------------------------------ geometry */

function buildUnits(): number[][] {
  const units: number[][] = [];
  for (let r = 0; r < 9; r += 1) {
    units.push(Array.from({ length: 9 }, (_, c) => r * 9 + c));
  }
  for (let c = 0; c < 9; c += 1) {
    units.push(Array.from({ length: 9 }, (_, r) => r * 9 + c));
  }
  for (let b = 0; b < 9; b += 1) {
    const top = Math.floor(b / 3) * 3;
    const left = (b % 3) * 3;
    units.push(
      Array.from(
        { length: 9 },
        (_, k) => (top + Math.floor(k / 3)) * 9 + left + (k % 3)
      )
    );
  }
  return units;
}

/** Rows 0–8, columns 9–17, boxes 18–26, each a list of nine cell indices. */
export const UNITS: readonly (readonly number[])[] = buildUnits();
const ROWS = UNITS.slice(0, 9);
const COLS = UNITS.slice(9, 18);
const BOXES = UNITS.slice(18, 27);

export function rowOf(cell: number): number {
  return Math.floor(cell / 9);
}
export function colOf(cell: number): number {
  return cell % 9;
}
export function boxOf(cell: number): number {
  return Math.floor(rowOf(cell) / 3) * 3 + Math.floor(colOf(cell) / 3);
}

const PEERS: readonly (readonly number[])[] = Array.from(
  { length: CELLS },
  (_, cell) => {
    const set = new Set<number>();
    for (const unit of [
      ROWS[rowOf(cell)],
      COLS[colOf(cell)],
      BOXES[boxOf(cell)],
    ]) {
      for (const other of unit ?? []) {
        if (other !== cell) {
          set.add(other);
        }
      }
    }
    return [...set];
  }
);

export function peersOf(cell: number): readonly number[] {
  return PEERS[cell] ?? [];
}

function bit(digit: number): number {
  return 1 << (digit - 1);
}

function popcount(mask: number): number {
  let n = 0;
  let m = mask;
  while (m) {
    n += 1;
    m &= m - 1;
  }
  return n;
}

function lowestDigit(mask: number): number {
  return Math.log2(mask & -mask) + 1;
}

/* -------------------------------------------------------------------- solver */

/** True when no digit appears twice in any row, column or box. */
export function hasNoConflicts(grid: readonly number[]): boolean {
  if (grid.length !== CELLS) {
    return false;
  }
  for (const unit of UNITS) {
    let seen = 0;
    for (const cell of unit) {
      const digit = grid[cell] ?? 0;
      if (digit < 0 || digit > 9) {
        return false;
      }
      if (digit !== 0) {
        if (seen & bit(digit)) {
          return false;
        }
        seen |= bit(digit);
      }
    }
  }
  return true;
}

interface SearchState {
  grid: number[];
  rows: number[];
  cols: number[];
  boxes: number[];
}

function startSearch(grid: readonly number[]): SearchState {
  const state: SearchState = {
    grid: grid.slice(),
    rows: new Array<number>(9).fill(0),
    cols: new Array<number>(9).fill(0),
    boxes: new Array<number>(9).fill(0),
  };
  for (let cell = 0; cell < CELLS; cell += 1) {
    const digit = grid[cell] ?? 0;
    if (digit) {
      state.rows[rowOf(cell)] = (state.rows[rowOf(cell)] ?? 0) | bit(digit);
      state.cols[colOf(cell)] = (state.cols[colOf(cell)] ?? 0) | bit(digit);
      state.boxes[boxOf(cell)] = (state.boxes[boxOf(cell)] ?? 0) | bit(digit);
    }
  }
  return state;
}

function candidatesAt(state: SearchState, cell: number): number {
  const used =
    (state.rows[rowOf(cell)] ?? 0) |
    (state.cols[colOf(cell)] ?? 0) |
    (state.boxes[boxOf(cell)] ?? 0);
  return ALL_DIGITS & ~used;
}

/** The empty cell with the fewest candidates, or -1 when the grid is full. */
function mostConstrained(state: SearchState): { cell: number; mask: number } {
  let best = -1;
  let bestMask = 0;
  let bestCount = 10;
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (state.grid[cell] === 0) {
      const mask = candidatesAt(state, cell);
      const count = popcount(mask);
      if (count < bestCount) {
        best = cell;
        bestMask = mask;
        bestCount = count;
        if (count <= 1) {
          break;
        }
      }
    }
  }
  return { cell: best, mask: bestMask };
}

function setDigit(
  state: SearchState,
  cell: number,
  digit: number,
  on: boolean
): void {
  const value = on ? digit : 0;
  state.grid[cell] = value;
  const flag = bit(digit);
  const r = rowOf(cell);
  const c = colOf(cell);
  const b = boxOf(cell);
  if (on) {
    state.rows[r] = (state.rows[r] ?? 0) | flag;
    state.cols[c] = (state.cols[c] ?? 0) | flag;
    state.boxes[b] = (state.boxes[b] ?? 0) | flag;
  } else {
    state.rows[r] = (state.rows[r] ?? 0) & ~flag;
    state.cols[c] = (state.cols[c] ?? 0) & ~flag;
    state.boxes[b] = (state.boxes[b] ?? 0) & ~flag;
  }
}

function digitOrder(mask: number, rng: Rng | null): number[] {
  const digits: number[] = [];
  for (let d = 1; d <= 9; d += 1) {
    if (mask & bit(d)) {
      digits.push(d);
    }
  }
  return rng ? shuffled(rng, digits) : digits;
}

/**
 * Depth-first search that collects up to `limit` solutions. With an `rng` the digits are
 * tried in random order, which is how the generator makes a random full grid.
 */
function search(
  state: SearchState,
  limit: number,
  found: number[][],
  rng: Rng | null
): void {
  if (found.length >= limit) {
    return;
  }
  const { cell, mask } = mostConstrained(state);
  if (cell === -1) {
    found.push(state.grid.slice());
    return;
  }
  for (const digit of digitOrder(mask, rng)) {
    setDigit(state, cell, digit, true);
    search(state, limit, found, rng);
    setDigit(state, cell, digit, false);
    if (found.length >= limit) {
      return;
    }
  }
}

/** Up to `limit` solutions of a grid (0 = empty). An invalid grid has none. */
export function solveAll(grid: readonly number[], limit = 2): number[][] {
  if (!hasNoConflicts(grid)) {
    return [];
  }
  const found: number[][] = [];
  search(startSearch(grid), limit, found, null);
  return found;
}

/** How many solutions the grid has, counted only up to `limit`. */
export function countSolutions(grid: readonly number[], limit = 2): number {
  return solveAll(grid, limit).length;
}

export function solve(grid: readonly number[]): number[] | null {
  return solveAll(grid, 1)[0] ?? null;
}

/** A random complete grid. */
export function randomSolution(rng: Rng): number[] {
  const found: number[][] = [];
  search(startSearch(new Array<number>(CELLS).fill(0)), 1, found, rng);
  return found[0] ?? new Array<number>(CELLS).fill(0);
}

/* --------------------------------------------------------------------- rater */

export interface Rating {
  /** Whether the rater's techniques were enough to finish the grid. */
  solved: boolean;
  /** 1–4 by the hardest technique used; 5 when the rater could not finish. */
  tier: number;
  hardest: Technique | null;
  /** The grid the rater reached: complete when `solved`, otherwise where it got stuck. */
  grid: number[];
  /** How many times each technique was applied. */
  counts: Record<Technique, number>;
}

interface Human {
  vals: number[];
  cand: number[];
}

function startHuman(grid: readonly number[]): Human {
  const human: Human = {
    vals: grid.slice(),
    cand: new Array<number>(CELLS).fill(0),
  };
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (human.vals[cell] === 0) {
      let mask = ALL_DIGITS;
      for (const peer of peersOf(cell)) {
        const digit = grid[peer] ?? 0;
        if (digit) {
          mask &= ~bit(digit);
        }
      }
      human.cand[cell] = mask;
    }
  }
  return human;
}

function place(human: Human, cell: number, digit: number): void {
  human.vals[cell] = digit;
  human.cand[cell] = 0;
  for (const peer of peersOf(cell)) {
    human.cand[peer] = (human.cand[peer] ?? 0) & ~bit(digit);
  }
}

/** Remove `mask` from a cell's candidates; true if anything changed. */
function eliminate(human: Human, cell: number, mask: number): boolean {
  const before = human.cand[cell] ?? 0;
  const after = before & ~mask;
  if (after === before) {
    return false;
  }
  human.cand[cell] = after;
  return true;
}

function nakedSingle(human: Human): boolean {
  for (let cell = 0; cell < CELLS; cell += 1) {
    const mask = human.cand[cell] ?? 0;
    if (human.vals[cell] === 0 && popcount(mask) === 1) {
      place(human, cell, lowestDigit(mask));
      return true;
    }
  }
  return false;
}

function hiddenSingle(human: Human): boolean {
  for (const unit of UNITS) {
    for (let digit = 1; digit <= 9; digit += 1) {
      const spots = unit.filter((cell) => (human.cand[cell] ?? 0) & bit(digit));
      const only = spots[0];
      if (spots.length === 1 && only !== undefined && human.vals[only] === 0) {
        place(human, only, digit);
        return true;
      }
    }
  }
  return false;
}

function cellsWithDigit(
  human: Human,
  unit: readonly number[],
  digit: number
): number[] {
  return unit.filter((cell) => (human.cand[cell] ?? 0) & bit(digit));
}

function allIn(
  cells: readonly number[],
  pick: (cell: number) => number
): number | null {
  const first = cells[0];
  if (first === undefined) {
    return null;
  }
  const key = pick(first);
  return cells.every((cell) => pick(cell) === key) ? key : null;
}

/** Remove a digit from every cell of `target` that is not also in `except`. */
function clearOutside(
  human: Human,
  target: readonly number[],
  except: readonly number[],
  digit: number
): boolean {
  let changed = false;
  for (const cell of target) {
    if (!except.includes(cell) && eliminate(human, cell, bit(digit))) {
      changed = true;
    }
  }
  return changed;
}

/** The row or column that holds every candidate spot, if there is exactly one. */
function sharedLine(spots: readonly number[]): readonly number[] | null {
  const row = allIn(spots, rowOf);
  if (row !== null) {
    return ROWS[row] ?? null;
  }
  const col = allIn(spots, colOf);
  return col === null ? null : (COLS[col] ?? null);
}

/** Pointing: a digit confined to one line of a box leaves the rest of that line. */
function pointing(human: Human, digit: number): boolean {
  let changed = false;
  for (const box of BOXES) {
    const spots = cellsWithDigit(human, box, digit);
    const line = spots.length > 1 ? sharedLine(spots) : null;
    if (line && clearOutside(human, line, box, digit)) {
      changed = true;
    }
  }
  return changed;
}

/** Claiming: a digit confined to one box within a line leaves the rest of that box. */
function claiming(human: Human, digit: number): boolean {
  let changed = false;
  for (const line of [...ROWS, ...COLS]) {
    const spots = cellsWithDigit(human, line, digit);
    const box = spots.length > 1 ? allIn(spots, boxOf) : null;
    if (box !== null && clearOutside(human, BOXES[box] ?? [], line, digit)) {
      changed = true;
    }
  }
  return changed;
}

function lockedCandidates(human: Human): boolean {
  for (let digit = 1; digit <= 9; digit += 1) {
    if (pointing(human, digit) || claiming(human, digit)) {
      return true;
    }
  }
  return false;
}

function combinations<T>(items: readonly T[], size: number): T[][] {
  if (size === 0) {
    return [[]];
  }
  const result: T[][] = [];
  for (let i = 0; i <= items.length - size; i += 1) {
    for (const rest of combinations(items.slice(i + 1), size - 1)) {
      result.push([items[i] as T, ...rest]);
    }
  }
  return result;
}

function nakedSubsetIn(
  human: Human,
  unit: readonly number[],
  size: number
): boolean {
  const open = unit.filter((cell) => {
    const count = popcount(human.cand[cell] ?? 0);
    return human.vals[cell] === 0 && count >= 2 && count <= size;
  });
  for (const group of combinations(open, size)) {
    const union = group.reduce(
      (mask, cell) => mask | (human.cand[cell] ?? 0),
      0
    );
    if (popcount(union) === size) {
      let changed = false;
      for (const cell of unit) {
        if (!group.includes(cell) && eliminate(human, cell, union)) {
          changed = true;
        }
      }
      if (changed) {
        return true;
      }
    }
  }
  return false;
}

function hiddenSubsetIn(
  human: Human,
  unit: readonly number[],
  size: number
): boolean {
  const spotsOf = (digit: number) =>
    unit.reduce(
      (mask, cell, index) =>
        (human.cand[cell] ?? 0) & bit(digit) ? mask | (1 << index) : mask,
      0
    );
  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((digit) => {
    const count = popcount(spotsOf(digit));
    return count >= 2 && count <= size;
  });
  for (const group of combinations(digits, size)) {
    const spots = group.reduce((mask, digit) => mask | spotsOf(digit), 0);
    if (popcount(spots) === size) {
      const keep = group.reduce((mask, digit) => mask | bit(digit), 0);
      let changed = false;
      unit.forEach((cell, index) => {
        if (
          spots & (1 << index) &&
          eliminate(human, cell, ALL_DIGITS & ~keep)
        ) {
          changed = true;
        }
      });
      if (changed) {
        return true;
      }
    }
  }
  return false;
}

function subsetStep(
  human: Human,
  find: (human: Human, unit: readonly number[], size: number) => boolean
): boolean {
  for (const size of [2, 3]) {
    for (const unit of UNITS) {
      if (find(human, unit, size)) {
        return true;
      }
    }
  }
  return false;
}

interface Line {
  index: number;
  crosses: number[];
}

/** Remove the digit from the cover lines, except in the two base lines of the X-Wing. */
function sweepCovers(
  human: Human,
  bases: readonly (readonly number[])[],
  covers: readonly (readonly number[])[],
  pair: readonly [Line, Line],
  digit: number
): boolean {
  let changed = false;
  for (const cross of pair[0].crosses) {
    for (const cell of covers[cross] ?? []) {
      const base = bases.findIndex((line) => line.includes(cell));
      const inPair = base === pair[0].index || base === pair[1].index;
      if (!inPair && eliminate(human, cell, bit(digit))) {
        changed = true;
      }
    }
  }
  return changed;
}

function xWingAlong(
  human: Human,
  bases: readonly (readonly number[])[],
  crossOf: (cell: number) => number,
  covers: readonly (readonly number[])[]
): boolean {
  for (let digit = 1; digit <= 9; digit += 1) {
    const lines: Line[] = bases
      .map((base, index) => ({
        index,
        crosses: cellsWithDigit(human, base, digit).map(crossOf),
      }))
      .filter((line) => line.crosses.length === 2);
    for (const [a, b] of combinations(lines, 2)) {
      if (
        a &&
        b &&
        a.crosses.join() === b.crosses.join() &&
        sweepCovers(human, bases, covers, [a, b], digit)
      ) {
        return true;
      }
    }
  }
  return false;
}

function xWing(human: Human): boolean {
  return (
    xWingAlong(human, ROWS, colOf, COLS) || xWingAlong(human, COLS, rowOf, ROWS)
  );
}

const STEPS: readonly {
  technique: Technique;
  apply: (human: Human) => boolean;
}[] = [
  { technique: "naked-single", apply: nakedSingle },
  { technique: "hidden-single", apply: hiddenSingle },
  { technique: "locked-candidates", apply: lockedCandidates },
  {
    technique: "naked-subset",
    apply: (human) => subsetStep(human, nakedSubsetIn),
  },
  {
    technique: "hidden-subset",
    apply: (human) => subsetStep(human, hiddenSubsetIn),
  },
  { technique: "x-wing", apply: xWing },
];

function emptyCounts(): Record<Technique, number> {
  return {
    "naked-single": 0,
    "hidden-single": 0,
    "locked-candidates": 0,
    "naked-subset": 0,
    "hidden-subset": 0,
    "x-wing": 0,
  };
}

/**
 * Solve the grid like a person, easiest technique first, and report the hardest one
 * needed. A dead end (a non-unique or very hard puzzle) is tier 5, `solved: false`.
 */
export function ratePuzzle(grid: readonly number[]): Rating {
  const human = startHuman(grid);
  const counts = emptyCounts();
  let hardest: Technique | null = null;
  let tier = 0;
  while (human.vals.includes(0)) {
    const step = STEPS.find((candidate) => candidate.apply(human));
    if (!step) {
      return {
        solved: false,
        tier: BEYOND_TIER,
        hardest,
        grid: human.vals,
        counts,
      };
    }
    counts[step.technique] += 1;
    if (TECHNIQUE_TIER[step.technique] > tier) {
      tier = TECHNIQUE_TIER[step.technique];
      hardest = step.technique;
    }
  }
  return {
    solved: true,
    tier: Math.max(tier, 1),
    hardest,
    grid: human.vals,
    counts,
  };
}

/** The tier a rating belongs to, as a difficulty name; null when beyond the rater. */
export function difficultyOfTier(tier: number): Difficulty | null {
  return DIFFICULTIES.find((name) => TIER_OF[name] === tier) ?? null;
}

/* ----------------------------------------------------------------- generator */

export interface GeneratedPuzzle {
  /** 81 digits, 0 for an empty cell. */
  puzzle: number[];
  solution: number[];
  rating: Rating;
  givens: number;
  requested: Difficulty;
  /** Whether the rating lands in the requested tier (expert accepts anything harder). */
  onTarget: boolean;
  seed: number;
  symmetric: boolean;
}

/** The cells removed together: a cell and, when symmetric, its 180° partner. */
function removalGroups(rng: Rng, symmetric: boolean): number[][] {
  const groups: number[][] = [];
  for (let cell = 0; cell < CELLS; cell += 1) {
    const partner = CELLS - 1 - cell;
    if (!symmetric) {
      groups.push([cell]);
    } else if (cell < partner) {
      groups.push([cell, partner]);
    } else if (cell === partner) {
      groups.push([cell]);
    }
  }
  return shuffled(rng, groups);
}

/** May the puzzle lose these cells and stay unique and within the tier limit? */
function canRemove(puzzle: number[], limit: number): boolean {
  if (countSolutions(puzzle, 2) !== 1) {
    return false;
  }
  return limit >= BEYOND_TIER || ratePuzzle(puzzle).tier <= limit;
}

function dig(
  solution: readonly number[],
  limit: number,
  stopAt: number,
  rng: Rng,
  symmetric: boolean
): number[] {
  const puzzle = solution.slice();
  let givens = CELLS;
  for (const group of removalGroups(rng, symmetric)) {
    if (givens - group.length < stopAt) {
      continue;
    }
    const held = group.map((cell) => puzzle[cell] ?? 0);
    for (const cell of group) {
      puzzle[cell] = 0;
    }
    if (canRemove(puzzle, limit)) {
      givens -= group.length;
    } else {
      group.forEach((cell, index) => {
        puzzle[cell] = held[index] ?? 0;
      });
    }
  }
  return puzzle;
}

interface Phase {
  /** The hardest tier the dig may use; BEYOND_TIER lifts the limit. */
  limit: number;
  accepts: (rating: Rating) => boolean;
  attempts: number;
}

/**
 * Easy, medium and hard dig under their own tier and accept only that tier. Expert first
 * looks for a puzzle that needs an X-Wing and the techniques below it; only if none turns
 * up does it accept a minimal puzzle that is harder still.
 */
function phasesFor(difficulty: Difficulty): Phase[] {
  const tier = TIER_OF[difficulty];
  if (difficulty !== "expert") {
    return [
      {
        limit: tier,
        accepts: (rating) => rating.tier === tier,
        attempts: MAX_ATTEMPTS,
      },
    ];
  }
  return [
    {
      limit: tier,
      accepts: (rating) => rating.tier === tier,
      attempts: XWING_ATTEMPTS,
    },
    {
      limit: BEYOND_TIER,
      accepts: (rating) => rating.tier >= tier,
      attempts: MAX_ATTEMPTS,
    },
  ];
}

/**
 * Make a unique-solution puzzle for a seed and difficulty.
 *
 * Starts from a random full grid and removes givens (in 180° pairs when `symmetric`),
 * keeping each removal only while the solution stays unique and the rater can still
 * finish within the tier limit. A grid whose result misses the requested tier is
 * discarded and another is tried, up to a fixed number of attempts; the closest result is
 * returned with `onTarget: false` if none matches, so the page can say so honestly.
 */
export function generatePuzzle(
  seed: number,
  difficulty: Difficulty,
  symmetric = true
): GeneratedPuzzle {
  const rng = createRng(seed);
  const stopAt = difficulty === "easy" ? EASY_STOP_AT : 0;
  const wanted = TIER_OF[difficulty];
  let best: GeneratedPuzzle | null = null;
  for (const phase of phasesFor(difficulty)) {
    for (let attempt = 0; attempt < phase.attempts; attempt += 1) {
      const solution = randomSolution(rng);
      const puzzle = dig(solution, phase.limit, stopAt, rng, symmetric);
      const rating = ratePuzzle(puzzle);
      const candidate: GeneratedPuzzle = {
        puzzle,
        solution,
        rating,
        givens: puzzle.filter((digit) => digit !== 0).length,
        requested: difficulty,
        onTarget: phase.accepts(rating),
        seed,
        symmetric,
      };
      if (candidate.onTarget) {
        return candidate;
      }
      const gap = Math.abs(rating.tier - wanted);
      if (!best || gap < Math.abs(best.rating.tier - wanted)) {
        best = candidate;
      }
    }
  }
  return best as GeneratedPuzzle;
}

/** The 81-character string form: digits, with `.` for an empty cell. */
export function toPuzzleString(grid: readonly number[]): string {
  return grid.map((digit) => (digit === 0 ? "." : String(digit))).join("");
}

const PUZZLE_CHARS = /^[0-9.]{81}$/;
const WHITESPACE = /\s+/g;

/** Reads the 81-character form; null when it is not one. */
export function fromPuzzleString(text: string): number[] | null {
  const clean = text.replace(WHITESPACE, "");
  if (!PUZZLE_CHARS.test(clean)) {
    return null;
  }
  return Array.from(clean, (char) => (char === "." ? 0 : Number(char)));
}

/** Cells whose entered digit disagrees with the solution. */
export function wrongCells(
  entries: readonly number[],
  solution: readonly number[]
): number[] {
  const wrong: number[] = [];
  entries.forEach((digit, cell) => {
    if (digit !== 0 && digit !== solution[cell]) {
      wrong.push(cell);
    }
  });
  return wrong;
}
// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the bit-mask solver
